/**
 * autismSSIProgress.js
 *
 * Computes clinician-facing progress for the Adult Autism Semi-Structured
 * Interview (SSI) stage from its Firestore assessment stage document.
 *
 * Progress is based on evidence decisions: a criterion is "complete" when the
 * clinician has set both `evidence_childhood` and `evidence_adulthood` to a
 * non-null value. 7 DSM criteria (A1-A3, B1-B4) contribute equally.
 *
 * This drives the DDI card progress bar on AutismPatientOverview and the
 * routing logic in developmentalDiagnosticInterviewUtils that determines
 * whether Start/Continue opens History or SSI.
 */

const SSI_CRITERION_IDS = ['A1', 'A2', 'A3', 'B1', 'B2', 'B3', 'B4'];

/**
 * @param {Object|null|undefined} ssiStageData - The full assessmentStages.semiStructuredInterview document
 * @returns {number} Integer 0-100
 */
export function computeSSIProgress(ssiStageData) {
  if (!ssiStageData) return 0;

  if (ssiStageData.status === 'completed') return 100;

  const criteria = ssiStageData.data?.criteria;
  if (!criteria || typeof criteria !== 'object') return 0;

  let completedCount = 0;
  for (const id of SSI_CRITERION_IDS) {
    const criterion = criteria[id];
    if (!criterion) continue;
    if (criterion.evidence_childhood != null && criterion.evidence_adulthood != null) {
      completedCount++;
    }
  }

  return Math.round((completedCount / SSI_CRITERION_IDS.length) * 100);
}

/**
 * Check whether a single criterion has both evidence decisions set.
 * Used by the SSI sidebar to show completion indicators.
 *
 * @param {Object|null|undefined} criterionData
 * @returns {boolean}
 */
export function isSSICriterionComplete(criterionData) {
  if (!criterionData) return false;
  return criterionData.evidence_childhood != null && criterionData.evidence_adulthood != null;
}
